import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, forkJoin, of } from 'rxjs';
import { environment } from '../environments/environmentCAS';

export interface DashboardStats {
  totalComparisons: number;
  totalUsers: number;
  comparisonsThisMonth: number;
  comparisonsThisWeek: number;
  averagePrice: number;
  mostPopularInsurer: string;
  recentComparisons: any[];
  insurerDistribution: { name: string; count: number; percentage: number }[];
  monthlyTrend: { month: string; count: number }[];
  priceRanges: { range: string; count: number }[];
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private readonly API_URL = environment.casApiUrl;

  constructor(private http: HttpClient) { }

  /**
   * Récupérer toutes les statistiques du dashboard
   */
  getDashboardStats(): Observable<DashboardStats> {
    return this.http.get<any[]>(`${this.API_URL}/api/historique-comparaisons-ia`).pipe(
      map(comparisons => this.calculateStats(comparisons))
    );
  }

  /**
   * Calculer les statistiques à partir des comparaisons
   */
  private calculateStats(comparisons: any[]): DashboardStats {

    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    // Comparaisons ce mois
    const comparisonsThisMonth = comparisons.filter(c => {
      const dateStr = c.date_comparaison || c.date || c.createdAt || c.dateCreation;
      if (!dateStr) return false;
      
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return false;
      
      return date.getMonth() === thisMonth && date.getFullYear() === thisYear;
    }).length;

    // Comparaisons cette semaine
    const comparisonsThisWeek = comparisons.filter(c => {
      const dateStr = c.date_comparaison || c.date || c.createdAt || c.dateCreation;
      if (!dateStr) return false;
      
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return false;
      
      return date >= oneWeekAgo;
    }).length;

    // Utilisateurs uniques
    // Supporte à la fois camelCase (userEmail) et snake_case (user_email)
    // et ignore les emails vides ou non définis
    const uniqueUsers = new Set(
      comparisons
        .map(c => c.userEmail || c.user_email)
        .filter((email: string | undefined) => !!email)
    ).size;

    // Prix moyen et distribution
    const allPrices: number[] = [];
    const insurerCounts: { [key: string]: number } = {};

    comparisons.forEach(c => {
      // Parser les résultats s'ils sont en string JSON
      let results = c.results;
      
      if (typeof results === 'string') {
        try {
          results = JSON.parse(results);
        } catch (e) {
          console.warn('⚠️ Impossible de parser results:', results);
          return;
        }
      }

      if (results && Array.isArray(results)) {
        results.forEach((r: any) => {
          // Extraire le prix - essayer plusieurs formats
          let prix = 0;
          
          // Format direct
          if (r.prix) prix = parseFloat(r.prix);
          else if (r.price) prix = parseFloat(r.price);
          else if (r.tarifMensuel) prix = parseFloat(r.tarifMensuel);
          else if (r.cotisationMensuelle) prix = parseFloat(r.cotisationMensuelle);
          
          // Format imbriqué dans garanties
          else if (r.garanties?.prix) prix = parseFloat(r.garanties.prix);
          else if (r.garanties?.price) prix = parseFloat(r.garanties.price);
          else if (r.garanties?.tarifMensuel) prix = parseFloat(r.garanties.tarifMensuel);
          
          // Extraire depuis une chaîne de texte (ex: "123.45€")
          else if (typeof r === 'string') {
            const match = r.match(/(\d+(?:[.,]\d+)?)\s*€/);
            if (match) prix = parseFloat(match[1].replace(',', '.'));
          }
          
          // Si l'assureur contient le prix (format: "Assureur, Produit, Prix")
          else {
            const insurerStr = r.assureur || r.insurerName || r.assurance || '';
            const priceMatch = insurerStr.match(/(\d+(?:[.,]\d+)?)\s*€/);
            if (priceMatch) {
              prix = parseFloat(priceMatch[1].replace(',', '.'));
            }
          }

          if (prix > 0 && !isNaN(prix)) {
            allPrices.push(prix);
          }

          // Extraire l'assureur - nettoyer le nom
          let insurer = r.assureur || r.insurerName || r.assurance || 'Inconnu';
          
          // Si le format est "Assureur, Produit, Niveau X", extraire juste l'assureur
          if (typeof insurer === 'string' && insurer.includes(',')) {
            insurer = insurer.split(',')[0].trim();
          }
          
          insurerCounts[insurer] = (insurerCounts[insurer] || 0) + 1;
        });
      }
    });


    const averagePrice = allPrices.length > 0 
      ? allPrices.reduce((a, b) => a + b, 0) / allPrices.length 
      : 0;

    const totalInsurers = Object.values(insurerCounts).reduce((a, b) => a + b, 0);
    const insurerDistribution = Object.entries(insurerCounts)
      .map(([name, count]) => ({
        name,
        count,
        percentage: totalInsurers > 0 ? (count / totalInsurers) * 100 : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    const mostPopularInsurer = insurerDistribution.length > 0 
      ? insurerDistribution[0].name 
      : 'Aucun';

    // Tendance mensuelle (6 derniers mois)
    const monthlyTrend = this.calculateMonthlyTrend(comparisons);

    // Tranches de prix
    const priceRanges = this.calculatePriceRanges(allPrices);

    // Comparaisons récentes (5 dernières)
    const recentComparisons = comparisons
      .sort((a, b) => new Date(b.date_comparaison).getTime() - new Date(a.date_comparaison).getTime())
      .slice(0, 5)
      .map(c => {
        return {
          userName: c.user_name || 'Utilisateur Inconnu',
          principalName: c.principal_name || '',
          principalFirstName: c.principal_first_name || '',
          totalResults: c.total_results || 0,
          date: c.date_comparaison,
          regimeObligatoire: c.regime_obligatoire || 'N/A',
        };
      });

    return {
      totalComparisons: comparisons.length,
      totalUsers: uniqueUsers,
      comparisonsThisMonth,
      comparisonsThisWeek,
      averagePrice: Math.round(averagePrice * 100) / 100,
      mostPopularInsurer,
      recentComparisons,
      insurerDistribution,
      monthlyTrend,
      priceRanges
    };
  }

  /**
   * Calculer la tendance mensuelle
   */
  private calculateMonthlyTrend(comparisons: any[]): { month: string; count: number }[] {
    const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    const now = new Date();
    const trend: { month: string; count: number }[] = [];

    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const month = date.getMonth();
      const year = date.getFullYear();
      
      const count = comparisons.filter(c => {
        const dateStr = c.date_comparaison || c.date || c.createdAt || c.dateCreation;
        if (!dateStr) return false;
        
        const cDate = new Date(dateStr);
        if (isNaN(cDate.getTime())) return false;
        
        return cDate.getMonth() === month && cDate.getFullYear() === year;
      }).length;

      trend.push({
        month: monthNames[month],
        count
      });
    }

    return trend;
  }

  /**
   * Calculer les tranches de prix
   */
  private calculatePriceRanges(prices: number[]): { range: string; count: number }[] {
    const ranges = [
      { range: '0-50€', min: 0, max: 50, count: 0 },
      { range: '51-100€', min: 51, max: 100, count: 0 },
      { range: '101-150€', min: 101, max: 150, count: 0 },
      { range: '151-200€', min: 151, max: 200, count: 0 },
      { range: '200€+', min: 201, max: Infinity, count: 0 }
    ];

    prices.forEach(price => {
      const range = ranges.find(r => price >= r.min && price <= r.max);
      if (range) range.count++;
    });

    return ranges.map(({ range, count }) => ({ range, count }));
  }
}
